/** Rota vs actual table for the Labour Management demo screen - the extended version of the
 * original "Today's rota vs actual" table, now including service period, labour cost,
 * status, notes/exception text and row-level actions. Row mutation (mark resolved / add
 * note) updates local component state only - see the header comment in `index.tsx`. */

import { useState } from 'react';
import {
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { RotaRow } from '../types';
import { StatusChip } from './StatusChip';

interface RotaTableProps {
  rows: RotaRow[];
  currencyCode?: string;
  onSelectSite: (site: string) => void;
  onMarkResolved: (id: string) => void;
  onAddNote: (id: string, note: string) => void;
  onRequestCover: (row: RotaRow) => void;
}

const RotaTable = ({ rows, currencyCode, onSelectSite, onMarkResolved, onAddNote, onRequestCover }: RotaTableProps) => {
  const { t } = useTranslation('common');
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [noteDraftRowId, setNoteDraftRowId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const currencyPrefix = currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? '\u20ac' : '\u00a3';

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRowId(null);
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('labourManagementSite', 'Site')}</TableCell>
            <TableCell>{t('labourManagementServicePeriod', 'Service period')}</TableCell>
            <TableCell>{t('labourManagementRole', 'Role')}</TableCell>
            <TableCell align="right">{t('labourManagementScheduledHours', 'Scheduled hours')}</TableCell>
            <TableCell align="right">{t('labourManagementActualHours', 'Actual hours')}</TableCell>
            <TableCell align="right">{t('labourManagementVariance', 'Variance')}</TableCell>
            <TableCell align="right">{t('labourManagementLabourCost', 'Labour cost')}</TableCell>
            <TableCell>{t('labourManagementStatusLabel', 'Status')}</TableCell>
            <TableCell>{t('labourManagementNotesException', 'Notes / exception')}</TableCell>
            <TableCell align="right">{t('labourManagementActions', 'Actions')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={10}>{t('labourManagementNoResults', 'No sites match the current filters.')}</TableCell></TableRow>
          )}
          {rows.map((row) => {
            const variance = Number((row.actual - row.scheduled).toFixed(1));
            return (
              <TableRow key={row.id}>
                <TableCell>
                  <Link component="button"
                    onClick={() => onSelectSite(row.site)}
                    underline="hover">
                    {row.site}
                  </Link>
                </TableCell>
                <TableCell>{row.servicePeriod}</TableCell>
                <TableCell>{row.role}</TableCell>
                <TableCell align="right">{row.scheduled}</TableCell>
                <TableCell align="right">{row.actual}</TableCell>
                <TableCell align="right">{variance > 0 ? `+${variance}` : variance}</TableCell>
                <TableCell align="right">{`${currencyPrefix}${row.labourCost.toFixed(0)}`}</TableCell>
                <TableCell>
                  <StatusChip status={row.resolved ? 'good' : row.status}
                    label={row.resolved
                      ? t('labourManagementResolved', 'Resolved')
                      : t(`labourManagementStatus${row.status}`, row.status)} />
                </TableCell>
                <TableCell>
                  {noteDraftRowId === row.id ? (
                    <TextField size="small"
                      value={noteDraft}
                      autoComplete="off"
                      onChange={(e) => setNoteDraft(e.target.value)}
                      onBlur={() => {
                        if (noteDraft.trim()) {
                          onAddNote(row.id, noteDraft.trim());
                        }
                        setNoteDraftRowId(null);
                        setNoteDraft('');
                      }} />
                  ) : (
                    <Tooltip title={row.note}>
                      <Typography variant="textXS"
                        sx={{ maxWidth: 220, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.note || '\u2013'}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small"
                    onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuRowId(row.id); }}
                    aria-label={t('labourManagementRowActions', 'Row actions')}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                  <Menu open={menuRowId === row.id}
                    anchorEl={menuAnchor}
                    onClose={closeMenu}>
                    <MenuItem onClick={() => { onSelectSite(row.site); closeMenu(); }}>
                      {t('labourManagementViewShiftDetail', 'View shift detail')}
                    </MenuItem>
                    <MenuItem onClick={() => { setNoteDraftRowId(row.id); setNoteDraft(row.note); closeMenu(); }}>
                      {t('labourManagementAddNote', 'Add note')}
                    </MenuItem>
                    <MenuItem disabled={row.resolved}
                      onClick={() => { onMarkResolved(row.id); closeMenu(); }}>
                      {t('labourManagementMarkResolved', 'Mark resolved')}
                    </MenuItem>
                    <MenuItem onClick={() => { onRequestCover(row); closeMenu(); }}>
                      {t('labourManagementRequestCover', 'Request cover')}
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RotaTable;
